import { apiFetch } from './client';

// SignupConfig mirrors signupConfigItem on the backend
// (internal/api/signups.go). All fields are present in API responses; the
// PATCH body in UpdateSignupConfigInput uses optional fields + explicit
// clear_* flags to express the tri-state nullable timestamps + capacity.
export interface SignupConfig {
  team_id: string;
  enabled: boolean;
  mode: 'open' | 'admin_approval' | 'waitlist_only';
  opens_at?: string;
  closes_at?: string;
  capacity: number | null;
  public_description: string;
  requires_birth_date: boolean;
  requires_phone: boolean;
  requires_guardian_when_minor: boolean;
  requires_experience: boolean;
  requires_shirt_size: boolean;
  requires_personnummer: boolean;
  consent_text: string;
  // PR2 — one-time signup fee. Both null = free signup; both set
  // together = paid signup (Stripe Checkout kicks off after submit).
  // fee_amount_minor is integer minor units (öre / cents); the SPA
  // converts to/from major units for the input.
  fee_amount_minor: number | null;
  fee_currency: 'SEK' | 'NOK' | 'DKK' | 'EUR' | 'USD' | null;
  // PR3 — waitlist. Null/0 = no waitlist; positive = overflow rooms
  // beyond main capacity land in status='waitlisted' until full.
  waitlist_capacity: number | null;
  // Per-applicant scoping — when set, the portal's "Find your club"
  // page filters the team out for non-matching applicants. All null
  // = no restriction (default for newly-created teams).
  min_birth_year: number | null;
  max_birth_year: number | null;
  gender_restriction: 'male' | 'female' | null;
}

export interface UpdateSignupConfigInput {
  enabled?: boolean;
  mode?: 'open' | 'admin_approval' | 'waitlist_only';
  opens_at?: string;
  clear_opens_at?: boolean;
  closes_at?: string;
  clear_closes_at?: boolean;
  capacity?: number;
  clear_capacity?: boolean;
  public_description?: string;
  requires_birth_date?: boolean;
  requires_phone?: boolean;
  requires_guardian_when_minor?: boolean;
  requires_experience?: boolean;
  requires_shirt_size?: boolean;
  requires_personnummer?: boolean;
  consent_text?: string;
  // Fee (PR2). Set both fee_amount_minor + fee_currency together to
  // enable a paid signup. clear_fee=true clears both back to null
  // (free); same tri-state pattern as clear_capacity / clear_opens_at.
  fee_amount_minor?: number;
  fee_currency?: 'SEK' | 'NOK' | 'DKK' | 'EUR' | 'USD';
  clear_fee?: boolean;
  // Waitlist (PR3). Same tri-state pattern as capacity.
  waitlist_capacity?: number;
  clear_waitlist?: boolean;
  // Applicant scoping. Each nullable pair follows the Clear* pattern —
  // omit to leave alone, set clear_* to reset back to null.
  min_birth_year?: number;
  clear_min_birth_year?: boolean;
  max_birth_year?: number;
  clear_max_birth_year?: boolean;
  gender_restriction?: 'male' | 'female';
  clear_gender_restriction?: boolean;
}

export async function getSignupConfig(
  token: string | null,
  slug: string,
  teamID: string,
): Promise<SignupConfig> {
  return apiFetch<SignupConfig>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/signup/config`,
    { token },
  );
}

export async function updateSignupConfig(
  token: string | null,
  slug: string,
  teamID: string,
  input: UpdateSignupConfigInput,
): Promise<SignupConfig> {
  return apiFetch<SignupConfig>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/signup/config`,
    { method: 'PATCH', body: input, token },
  );
}

// Signup mirrors signupItem on the backend. PII fields come through verbatim
// because the admin queue lives behind the Bearer-token auth wall.
export type SignupStatus =
  | 'submitted'
  | 'awaiting_payment'
  | 'confirmed'
  | 'waitlisted'
  | 'rejected'
  | 'cancelled';

export interface Signup {
  id: string;
  team_id: string;
  status: SignupStatus;
  source: 'public' | 'admin_invite';
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  birth_date?: string;
  personnummer?: string;
  guardian_first_name?: string;
  guardian_last_name?: string;
  guardian_email?: string;
  guardian_phone?: string;
  experience_level?: string;
  shirt_size?: string;
  notes?: string;
  consent_at?: string;
  submitted_at: string;
  decided_at?: string;
  decided_by_user_id?: string;
  decision_note?: string;
  member_id?: string;
  // True when the authenticated caller authored this application (as the
  // applicant or as the guardian who submitted it). The queue hides the
  // approve/reject/promote actions on these rows; the backend enforces the
  // same separation of duties (403 forbidden_self_decision).
  is_own_signup: boolean;
}

export interface ListSignupsResponse {
  signups: Signup[];
}

export async function listTeamSignups(
  token: string | null,
  slug: string,
  teamID: string,
  status?: SignupStatus | '',
): Promise<ListSignupsResponse> {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiFetch<ListSignupsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/teams/${encodeURIComponent(teamID)}/signups${qs}`,
    { token },
  );
}

// SignupSummaryRow is one team row of the cross-team Anmälningar overview.
// Mirrors signupSummaryItem on the backend (internal/api/signups.go).
export interface SignupSummaryRow {
  team_id: string;
  team_name: string;
  team_color: string;
  category_id?: string;
  category_name?: string;
  enabled: boolean;
  mode: 'open' | 'admin_approval' | 'waitlist_only';
  // Signup window (RFC3339), omitted when unbounded. The list derives the
  // display status (open / scheduled / closed) from enabled + these against
  // "now" — enabled alone is not the whole story (a future opens_at means the
  // team is scheduled, not open yet).
  opens_at?: string;
  closes_at?: string;
  capacity: number | null;
  waitlist_capacity: number | null;
  fee_amount_minor: number | null;
  fee_currency: 'SEK' | 'NOK' | 'DKK' | 'EUR' | 'USD' | null;
  submitted_count: number;
  awaiting_count: number;
  confirmed_count: number;
  waitlisted_count: number;
  rejected_count: number;
  cancelled_count: number;
  // Current roster size (active team_member links). Distinct from
  // confirmed_count — see the merged Teams page (manual/imported members have
  // no signup; a confirmed signup whose member left isn't a current member).
  member_count: number;
}

export interface ListSummaryResponse {
  teams: SignupSummaryRow[];
}

export async function listSignupSummary(
  token: string | null,
  slug: string,
): Promise<ListSummaryResponse> {
  return apiFetch<ListSummaryResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/signups/summary`,
    { token },
  );
}

export async function cancelSignup(
  token: string | null,
  slug: string,
  signupID: string,
  note?: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/signups/${encodeURIComponent(signupID)}/cancel`,
    { method: 'POST', body: note ? { note } : {}, token },
  );
}

// Approve moves a submitted signup to confirmed; the backend runs the
// member + team_member auto-link inside the same tx. Returns the updated
// Signup so the caller can patch the row in the queue without a refetch.
export async function approveSignup(
  token: string | null,
  slug: string,
  signupID: string,
  note?: string,
): Promise<Signup> {
  return apiFetch<Signup>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/signups/${encodeURIComponent(signupID)}/approve`,
    { method: 'POST', body: note ? { note } : {}, token },
  );
}

// Reject marks a submitted signup as rejected. Terminal — to revive, the
// applicant must submit again.
export async function rejectSignup(
  token: string | null,
  slug: string,
  signupID: string,
  note?: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/signups/${encodeURIComponent(signupID)}/reject`,
    { method: 'POST', body: note ? { note } : {}, token },
  );
}

// reopenSignup flips a 'rejected' signup back to 'submitted', clearing
// the decision metadata. Used by the admin queue's "undo reject" action
// to recover from a misclick.
export async function reopenSignup(
  token: string | null,
  slug: string,
  signupID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/signups/${encodeURIComponent(signupID)}/reopen`,
    { method: 'POST', body: {}, token },
  );
}

// PromoteFromWaitlist moves a 'waitlisted' signup forward — to
// 'confirmed' (free + open mode, with member auto-link),
// 'submitted' (admin_approval), or 'awaiting_payment' (paid). The
// admin SPA wires this to the "Promote" row action on waitlisted rows.
export async function promoteSignup(
  token: string | null,
  slug: string,
  signupID: string,
  note?: string,
): Promise<Signup> {
  return apiFetch<Signup>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/signups/${encodeURIComponent(signupID)}/promote`,
    { method: 'POST', body: note ? { note } : {}, token },
  );
}
